"use client"

import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { T } from "@/i18n"
import DOMPurify from 'dompurify';

interface EmailBuilderProps {
  initialHtml: string
  onChange: (html: string) => void
  entityType?: string
  availableFields: Array<{ name: string; label: string }>
}

export function EmailBuilder({ initialHtml, onChange, entityType, availableFields }: EmailBuilderProps) {
  const [html, setHtml] = useState(initialHtml)
  const [activeTab, setActiveTab] = useState("visual")

  useEffect(() => {
    setHtml(initialHtml)
  }, [initialHtml])

  const handleHtmlChange = (newHtml: string) => {
    setHtml(newHtml)
    onChange(newHtml)
  }

  const insertField = (field: { name: string; label: string }) => {
    const placeholder = `{{${field.name}}}`

    // For visual editor, we need to use the editor's API
    if (activeTab === "html") {
      // This would depend on the specific email editor implementation
      // For now, we'll just append to the HTML
      const updatedHtml = html + ` ${placeholder}`
      setHtml(updatedHtml)
      onChange(updatedHtml)
    }
  }

  return (
    <div className="space-y-4">
      <Tabs defaultValue="visual" onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="visual">
            <T tKey="operations.templates.editor.visual" />
          </TabsTrigger>
          <TabsTrigger value="html">
            <T tKey="operations.templates.editor.html" />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="visual" className="p-0">
        <div
          dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(initialHtml)}}
          />
        </TabsContent>

        <TabsContent value="html" className="p-0">
          <Card>
            <CardContent className="p-0">
              <textarea
                className="w-full min-h-[400px] p-4 font-mono text-sm border-0 focus:outline-none"
                value={html}
                onChange={(e) => handleHtmlChange(e.target.value)}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div>
        <h3 className="text-sm font-medium mb-2">
          <T tKey="operations.templates.availableFields" />
          {entityType && ` (${entityType})`}
        </h3>
        <div className="flex flex-wrap gap-2">
          {availableFields.map((field) => (
            <Button key={field.name} variant="outline" size="sm" onClick={() => insertField(field)} className="text-xs">
              {field.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
